import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAdminPurchases, useResendPurchaseEmail, AdminPurchase } from '@/hooks/admin/useAdminPurchases';
import {
    ArrowLeftIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CreditCardIcon,
    EnvelopeIcon,
    CheckCircleIcon,
} from '@heroicons/react/24/outline';

function getStatusColor(status: string): string {
    switch (status) {
        case 'completed':
            return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
        case 'pending':
            return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
        case 'failed':
            return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
        case 'refunded':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
}

function PurchaseRow({
    purchase,
    onResendEmail,
    isResending,
}: {
    purchase: AdminPurchase;
    onResendEmail: (id: number) => void;
    isResending: boolean;
}) {
    const date = new Date(purchase.createdAt);
    const emailSentAt = purchase.confirmationEmailSentAt
        ? new Date(purchase.confirmationEmailSentAt)
        : null;

    return (
        <tr className="border-b border-gray-200 dark:border-gray-700">
            <td className="px-4 py-3 text-gray-900 dark:text-white">
                {purchase.userEmail}
            </td>
            <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">
                {purchase.amountFormatted}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {purchase.creditsAdded} credits
            </td>
            <td className="px-4 py-3">
                <span className={`rounded-sm px-2 py-1 text-xs font-medium ${getStatusColor(purchase.paymentStatus)}`}>
                    {purchase.paymentStatusLabel}
                </span>
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {date.toLocaleDateString()} {date.toLocaleTimeString()}
            </td>
            <td className="px-4 py-3">
                {purchase.paymentStatus === 'completed' ? (
                    <div className="flex items-center gap-2">
                        {emailSentAt ? (
                            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircleIcon className="h-4 w-4" />
                                Sent
                            </span>
                        ) : (
                            <span className="text-xs text-gray-400">Not sent</span>
                        )}
                        <button
                            type="button"
                            onClick={() => onResendEmail(purchase.id)}
                            disabled={isResending}
                            className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                        >
                            <EnvelopeIcon className="h-3 w-3" />
                            {emailSentAt ? 'Resend' : 'Send'}
                        </button>
                    </div>
                ) : (
                    <span className="text-xs text-gray-400">-</span>
                )}
            </td>
        </tr>
    );
}

export function AdminPurchasesPage() {
    usePageTitle('Admin - Purchases');

    const [page, setPage] = useState(1);
    const limit = 20;

    const { data, isLoading } = useAdminPurchases(page, limit);
    const resendEmail = useResendPurchaseEmail();

    const handleResendEmail = (purchaseId: number) => {
        resendEmail.mutate(purchaseId, {
            onSuccess: () => {
                toast.success('Confirmation email sent successfully');
            },
            onError: () => {
                toast.error('Failed to send confirmation email');
            },
        });
    };

    if (isLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    return (
        <>
            <div className="flex items-center gap-4">
                <Link
                    to="/admin"
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to Admin
                </Link>
            </div>

            <PageHeader title="Purchases" />

            {/* Purchases Table */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-secondary">
                {data?.purchases && data.purchases.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            User
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Amount
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Credits
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Date
                                        </th>
                                        <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                            Email
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.purchases.map((purchase) => (
                                        <PurchaseRow
                                            key={purchase.id}
                                            purchase={purchase}
                                            onResendEmail={handleResendEmail}
                                            isResending={resendEmail.isPending}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Showing {(page - 1) * limit + 1} to{' '}
                                {Math.min(page * limit, data.total)} of {data.total} purchases
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                    Previous
                                </Button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Page {page} of {data.totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= data.totalPages}
                                >
                                    Next
                                    <ChevronRightIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <CreditCardIcon className="mb-2 h-8 w-8" />
                        <p>No purchases found</p>
                    </div>
                )}
            </div>
        </>
    );
}
